import {
  DEFAULT_NAV_ACCENT_COLOR,
  DEFAULT_SCHOOL_FOOTER_COLOR,
  DEFAULT_SCHOOL_PRIMARY_COLOR,
} from '../common/ui-constants';
import { UserPermissions } from '../models/user-permissions.model';
import { WebsiteBrand } from '../models/website-brand.model';

/**
 * View models for the revamped header/footer. The containers build these VMs and
 * handle outputs; the components stay presentational.
 */

export interface OchNavLink {
  id: string;
  /** i18n key for platform links; `label` is the raw text for custom links. */
  labelKey?: string;
  label?: string;
  /** A link has exactly one of routerLink (internal) or href (external); neither = action. */
  routerLink?: string;
  href?: string;
  target?: '_blank' | '_self';
  custom?: boolean;
}

export interface OchHeaderVm {
  schoolName: string;
  logoUrl?: string;
  logoLetter: string;
  primary: string;
  accent: string;
  footer: string;
  /** False on SSO / landing-page mode: hides logo + nav, keeps the right cluster. */
  showNav: boolean;
  showBurger: boolean;
  loggedIn: boolean;
  loggedOut: boolean;
  avatarUrl?: string;
  avatarInitials: string;
  userName: string;
  userEmail: string;
  /** No admin role — shows the student account-menu items. */
  isStudent: boolean;
  showMyCertificates: boolean;
  showAffiliateProgram: boolean;
  showCreateYourCourse: boolean;
  showTrial: boolean;
  trialDaysLeft: number;
  trialPercent: number;
  showUpgrade: boolean;
  showAiAssistant: boolean;
  showNotifications: boolean;
  unreadCount: number;
  showTestPill: boolean;
  /** 0-100, or null when not on a course. */
  courseProgress: number | null;
  showSearch: boolean;
}

/** Resume card in the account menu (lazily loaded on menu open). */
export interface OchContinueWatching {
  routerLink: string;
  courseTitle: string;
  percent: number | null;
}

export interface OchFooterColumn {
  heading: string;
  links: { label: string; href: string }[];
}

export interface OchFooterSocial {
  /** facebook | x | youtube | instagram | linkedin */
  network: string;
  href: string;
}

export interface OchFooterLanguage {
  code: string;
  name: string;
  flagUrl: string;
}

/** A lesson hit from the OpenSearch-backed SearchComponent (only the fields we route on). */
export interface OchSearchHit {
  courseId?: string;
  sectionId?: string;
  seqNo?: number;
}

export interface OchFooterVm {
  schoolName: string;
  logoUrl?: string;
  logoLetter: string;
  primary: string;
  accent: string;
  footer: string;
  columns: OchFooterColumn[];
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;
  legalDisclosureUrl?: string;
  socials: OchFooterSocial[];
  copyright?: string;
  showNewsletterCta: boolean;
  showLanguage: boolean;
  currentLanguage: string;
  languages: OchFooterLanguage[];
  showSecureCheckout: boolean;
  /** Guarantee days, or null when the tenant offers none. */
  guaranteeDays: number | null;
}

/** Mirrors the ADMIN check of AllowRoleDirective / DenyRoleDirective. */
export function hasRevampAdminRole(permissions: UserPermissions | undefined): boolean {
  return !!(
    permissions?.isAdmin ||
    permissions?.customRoles?.permissions?.['ADMIN'] === true ||
    permissions?.customRoles?.permissions?.Admin?.allAllowed
  );
}

interface RevampBrandColors {
  primary: string;
  accent: string;
  footer: string;
}

/** Design defaults (Aurora palette) used when a tenant never customized their brand. */
const REVAMP_DEFAULT_PRIMARY = DEFAULT_SCHOOL_PRIMARY_COLOR;

/**
 * Resolves the revamp brand colors. Tenants who never explicitly customized their
 * branding (themeCustomized falsy) get the Aurora design palette; tenants who saved
 * a brand keep it verbatim — including a deliberate choice of the old platform
 * defaults (#11294f / #EB3B3B), which are now honoured like any other colour.
 */
export function resolveRevampBrand(
  brand: WebsiteBrand | undefined,
  footerColor: string | undefined,
): RevampBrandColors {
  const customized = !!brand?.themeCustomized;

  const primary = customized && brand?.primaryColor ? brand.primaryColor : REVAMP_DEFAULT_PRIMARY;

  // Customized: their accent (or fall back to their primary); otherwise the design nav accent.
  const accent = customized
    ? (brand?.accentColor || brand?.primaryColor || DEFAULT_NAV_ACCENT_COLOR)
    : DEFAULT_NAV_ACCENT_COLOR;

  return {
    primary,
    accent,
    footer: footerColor || DEFAULT_SCHOOL_FOOTER_COLOR,
  };
}

/**
 * Relative luminance (0-1) of a CSS color. Fast path parses hex colors
 * (tenant brand colors are validated hex); anything else falls back to a
 * DOM probe so named/rgb colors still work.
 */
export function revampLuminance(cssColor: string): number {
  const rgb = parseHexColor(cssColor) ?? probeColor(cssColor);
  const [r, g, b] = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseHexColor(color: string): number[] | null {
  const m = color?.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function probeColor(cssColor: string): number[] {
  const probe = document.createElement('div');
  probe.style.color = cssColor;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const parsed = getComputedStyle(probe).color.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0];
  probe.remove();
  return parsed.slice(0, 3);
}
